// src/pages/website/BlogManager.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Button, 
  Box, 
  Typography, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

function BlogManager() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'blog_posts'));
        setPosts(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Erreur lors du chargement des articles", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, []);

  const handleDelete = async () => {
    if (selectedPost) {
      try {
        await deleteDoc(doc(db, 'blog_posts', selectedPost.id));
        setPosts(posts.filter(post => post.id !== selectedPost.id));
      } catch (error) {
        console.error("Erreur lors de la suppression", error);
      } finally {
        setDeleteOpen(false);
      }
    }
  };

  const columns = [
    { field: 'title', headerName: 'Titre', width: 300 },
    { field: 'author', headerName: 'Auteur', width: 200 },
    { 
      field: 'createdAt', 
      headerName: 'Date', 
      width: 150,
      valueGetter: (params) => {
        // Protection contre row undefined
        if (!params.row) return 'N/A';
        
        const createdAt = params.row.createdAt;
        
        if (!createdAt) return 'N/A';
        
        try {
          if (typeof createdAt.toDate === 'function') {
            return createdAt.toDate().toLocaleDateString('fr-FR');
          }
          
          if (createdAt instanceof Date) {
            return createdAt.toLocaleDateString('fr-FR');
          }
        } catch (e) {
          console.error('Erreur de conversion de date', e);
        }
        
        return 'N/A';
      }
    },
    { 
      field: 'actions', 
      headerName: 'Actions', 
      width: 150,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => navigate(`/website/blog/edit/${params.row.id}`)}>
            <EditIcon color="primary" />
          </IconButton>
          <IconButton onClick={() => {
            setSelectedPost(params.row);
            setDeleteOpen(true);
          }}>
            <DeleteIcon color="error" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Gestion du Blog</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/website/blog/new')}
        >
          Nouvel Article
        </Button>
      </Box>

      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={posts}
          columns={columns}
          pageSize={10}
          getRowId={(row) => row.id}
          loading={loading}
          components={{
            LoadingOverlay: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.7)'
              }}>
                <CircularProgress />
              </Box>
            )
          }}
        />
      </div>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir supprimer l'article "{selectedPost?.title}" ?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Annuler</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BlogManager;